/**
 * BulkCRSFixDialog — Feb 2026 · Iter87 · Portfolio-level "Bulk
 * Re-Project CRS" action.
 *
 * Shows a table of legacy projects whose CRS is either missing or
 * incomplete. For every project with a country field, we fetch the
 * recommended UTM zone from `/api/gis/crs/country-defaults?country=…`
 * and let the user apply the healing patch to all selected projects
 * with one click.
 *
 * Backend impact: uses the existing `PATCH /api/projects/:pid` route,
 * which was extended in this pass to accept `epsg`, `datum`,
 * `utm_zone`, `coordinate_system` (see backend/models/project.py).
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { Wrench, CheckCircle, Warning } from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

// Reuses the same classifier as the CRS Health chip so the two stay in sync.
function needsCRS(p) {
  const c = p.country || (p.location || {})?.country;
  const e = p.epsg || (p.crs_config || {})?.epsg;
  const d = p.datum || p.vertical_datum;
  if (c && e && d) return false;
  return !!c || !e;
}

export default function BulkCRSFixDialog({ open, onOpenChange, projects }) {
  const qc = useQueryClient();
  const eligible = useMemo(
    () => (projects || []).filter(needsCRS),
    [projects]
  );
  const [suggestions, setSuggestions] = useState({}); // id → {epsg, datum, utm_zone, coordinate_system, source_country}
  const [selected, setSelected]       = useState({}); // id → bool
  const [applying, setApplying]       = useState(false);
  const [results, setResults]         = useState(null); // { ok: n, fail: n, details: [] }

  // ── When dialog opens, fetch defaults for every eligible project ──
  // Iter88 · Feb 2026 · switched from serial for-loop to Promise.all
  // with a hand-rolled concurrency limit (4 in-flight lookups). Keeps
  // portfolios of 50+ projects snappy while still being courteous to
  // the country-defaults endpoint.
  useEffect(() => {
    if (!open) { setSuggestions({}); setSelected({}); setResults(null); return; }
    let alive = true;
    (async () => {
      const CONCURRENCY = 4;
      const map = {};
      const sel = {};
      const fetchOne = async (p) => {
        const country = p.country || (p.location || {})?.country;
        if (!country) { map[p.id] = { error: "No country on project" }; return; }
        try {
          const r = await api.get("/gis/crs/country-defaults",
            { params: { country } });
          if (r.data?.found) {
            map[p.id] = {
              epsg:              r.data.epsg,
              datum:             r.data.preferred_datum,
              utm_zone:          r.data.utm_zone,
              coordinate_system: r.data.coordinate_system,
              source_country:    r.data.country,
              confidence:        r.data.confidence,
            };
            sel[p.id] = true;
          } else {
            map[p.id] = { error: `No default for "${country}"` };
          }
        } catch (e) {
          map[p.id] = { error: e?.message || "lookup failed" };
        }
      };
      // Consume the queue with `CONCURRENCY` parallel workers.
      const queue = [...eligible];
      const workers = Array.from({ length: Math.min(CONCURRENCY, queue.length) },
        async () => {
          while (queue.length) {
            const p = queue.shift();
            if (!p) break;
            if (!alive) break;
            await fetchOne(p);
          }
        });
      await Promise.all(workers);
      if (alive) { setSuggestions(map); setSelected(sel); }
    })();
    return () => { alive = false; };
  }, [open, eligible]);

  // ── Apply — patch each selected project sequentially so we can
  // report per-project results deterministically. ────────────────────
  const applyAll = async () => {
    setApplying(true);
    const details = [];
    let ok = 0, fail = 0;
    for (const p of eligible) {
      if (!selected[p.id]) continue;
      const s = suggestions[p.id];
      if (!s || s.error) { fail++; details.push({ id: p.id, name: p.name, ok: false, msg: s?.error || "no suggestion" }); continue; }
      try {
        await api.patch(`/projects/${p.id}`, {
          epsg: s.epsg, datum: s.datum,
          utm_zone: s.utm_zone,
          coordinate_system: s.coordinate_system,
        });
        ok++; details.push({ id: p.id, name: p.name, ok: true, msg: `→ EPSG:${s.epsg}` });
      } catch (e) {
        fail++; details.push({ id: p.id, name: p.name, ok: false, msg: e?.message || "patch failed" });
      }
    }
    setResults({ ok, fail, details });
    setApplying(false);
    if (ok) qc.invalidateQueries({ queryKey: ["projects"] });
    if (ok && !fail) toast.success(`Re-projected ${ok} project${ok === 1 ? "" : "s"}`);
    else if (ok && fail) toast.warning(`${ok} succeeded, ${fail} failed`);
    else if (fail) toast.error(`All ${fail} projects failed to re-project`);
  };

  const anySelected = Object.values(selected).some(Boolean);
  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-3xl max-h-[85vh] overflow-y-auto"
                     data-testid="bulk-crs-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-widest uppercase text-sm flex items-center gap-2">
            <Wrench size={16} className="text-brand-blue" weight="duotone" />
            Bulk Re-Project CRS
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Heal legacy projects that were created before country →
            EPSG auto-fill. Each row shows the recommended UTM zone
            based on the project&apos;s country. Uncheck any project you
            want to keep as-is; click Apply to write the CRS fields.
          </DialogDescription>
        </DialogHeader>

        {eligible.length === 0 && (
          <div className="text-center py-8 text-slate-500 text-[13px]">
            No projects need CRS attention right now. 🎉
          </div>
        )}

        {eligible.length > 0 && !results && (
          <div className="border rounded-md divide-y divide-slate-100">
            {eligible.map((p) => {
              const s = suggestions[p.id];
              const country = p.country || (p.location || {})?.country || "—";
              const hasErr = !s || s.error;
              return (
                <div key={p.id} className="px-3 py-2 flex items-center gap-3 text-[12px]"
                       data-testid={`bulk-crs-row-${p.id}`}>
                  <input type="checkbox" className="rounded border-slate-300"
                            data-testid={`bulk-crs-select-${p.id}`}
                            disabled={hasErr}
                            checked={!!selected[p.id]}
                            onChange={(e) => setSelected((sel) => ({ ...sel, [p.id]: e.target.checked }))} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-800 truncate">{p.name}</div>
                    <div className="text-[10.5px] text-slate-500">
                      Country: <span className="font-mono">{country}</span>
                      {" · Current: "}
                      <span className="font-mono">{p.epsg ? `EPSG:${p.epsg}` : "—"} · {p.datum || "no datum"}</span>
                    </div>
                  </div>
                  <div className="text-right min-w-[140px]">
                    {!s && <span className="text-[10.5px] text-slate-400 italic">Looking up…</span>}
                    {s?.error && (
                      <span className="text-[10.5px] text-rose-600 inline-flex items-center gap-1"
                             data-testid={`bulk-crs-error-${p.id}`}>
                        <Warning size={11} weight="fill" /> {s.error}
                      </span>
                    )}
                    {s && !s.error && (
                      <div data-testid={`bulk-crs-suggestion-${p.id}`}>
                        <div className="text-[11px] text-emerald-700 font-semibold">
                          → EPSG:{s.epsg}
                        </div>
                        <div className="text-[10px] text-slate-500 font-mono truncate">
                          {s.coordinate_system}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {results && (
          <div className="border rounded-md divide-y divide-slate-100"
                data-testid="bulk-crs-results">
            <div className="px-3 py-2 flex items-center gap-2 bg-slate-50 text-[12px] font-semibold">
              <CheckCircle size={14} className="text-emerald-600" weight="fill" />
              Applied {results.ok} · Failed {results.fail}
            </div>
            {results.details.map((d) => (
              <div key={d.id}
                    className={`px-3 py-1.5 flex items-center gap-2 text-[11.5px] ${d.ok ? "text-slate-700" : "text-rose-700"}`}
                    data-testid={`bulk-crs-result-${d.id}`}>
                <span className={`inline-block w-1.5 h-1.5 rounded-full ${d.ok ? "bg-emerald-500" : "bg-rose-500"}`}></span>
                <span className="truncate flex-1">{d.name}</span>
                <span className="text-[10.5px] font-mono text-slate-500">{d.msg}</span>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
                     data-testid="bulk-crs-cancel">
            {results ? "Close" : "Cancel"}
          </Button>
          {!results && eligible.length > 0 && (
            <Button
              onClick={applyAll}
              disabled={applying || !anySelected}
              data-testid="bulk-crs-apply"
              className="bg-brand-blue hover:bg-brand-blueDeep text-white"
            >
              {applying ? "Applying…" : `Apply to ${selectedCount} project${selectedCount === 1 ? "" : "s"}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
